import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel,
  MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { getSpaCommunicationPreferences, updateSpaCommunicationPreferences } from '../APIs/SpaAPI';
import { usePermissions } from '../contexts/PermissionsContext';
import useSpaCommunicationCapabilities from '../hooks/useSpaCommunicationCapabilities';
import SpaCommunicationChannels from './SpaCommunicationChannels';
import { localizedErrorMessage } from '../utils/italianErrorMessage';

const fields = ['preferredLanguage', 'emailConsent', 'smsConsent', 'whatsappConsent'];
export function communicationValues(data) {
  if (!['IT', 'EN'].includes(data?.preferredLanguage)
    || fields.slice(1).some((field) => typeof data?.[field] !== 'boolean')) {
    throw new Error('Invalid communication preferences response');
  }
  return Object.fromEntries(fields.map((field) => [field, data[field]]));
}

export default function SpaCommunicationPreferences({ clientId, token, tenant }) {
  const { t } = useTranslation();
  const ct = useCallback((key) => t(`spa.communications.${key}`), [t]);
  const { can, loading: permissionsLoading } = usePermissions();
  const view = !permissionsLoading && can('spa-clients:view');
  const manage = view && can('spa-clients:manage');
  const capabilities = useSpaCommunicationCapabilities(token, tenant, !permissionsLoading && view && can('spa-communications:view'));
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [reload, setReload] = useState(0);
  const mounted = useRef(false);
  const savingRef = useRef(false);
  const scope = `${clientId}:${token}:${tenant}:${manage}`;
  const currentScope = useRef(scope); currentScope.current = scope;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setSaved(null);
    setDraft(null);
    setSuccess(false);
    setError('');
    if (!view || !clientId || !token || !tenant) return undefined;
    setLoading(true);
    getSpaCommunicationPreferences(clientId, token, tenant).then((data) => {
      const values = communicationValues(data);
      if (!cancelled) { setSaved(values); setDraft(values); }
    }).catch((err) => {
      if (!cancelled) setError(err);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, token, tenant, view, reload]);

  const dirty = saved && draft && fields.some((field) => saved[field] !== draft[field]);
  const change = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setSuccess(false);
  };
  const save = async (event) => {
    event.preventDefault();
    if (!manage || !dirty || savingRef.current) return;
    savingRef.current = true;
    const submittedScope = scope;
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      const payload = { preferredLanguage: draft.preferredLanguage, emailConsent: draft.emailConsent };
      const values = communicationValues(await updateSpaCommunicationPreferences(clientId, payload, token, tenant));
      if (mounted.current && currentScope.current === submittedScope) { setSaved(values); setDraft(values); setSuccess(true); }
    } catch (err) {
      if (mounted.current && currentScope.current === submittedScope) setError(localizedErrorMessage(err, ct('saveError')));
    } finally {
      savingRef.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  if (!view) return null;
  return (
    <Box component="section" sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>{ct('title')}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{ct(saved ? 'saveError' : 'loadError')} {localizedErrorMessage(error, ct(saved ? 'saveError' : 'loadError'))}</Alert>}
      {loading ? <CircularProgress size={24} aria-label={ct('loading')} /> : !draft ? (
        <Button startIcon={<RefreshIcon />} onClick={() => setReload((value) => value + 1)}>{ct('retry')}</Button>
      ) : (
        <Box component="form" onSubmit={save}>
          <Stack spacing={2}>
            <TextField select label={ct('language')} value={draft.preferredLanguage}
              onChange={(event) => change('preferredLanguage', event.target.value)} disabled={!manage || saving} sx={{ maxWidth: 320 }}>
              <MenuItem value="IT">{ct('italian')}</MenuItem>
              <MenuItem value="EN">{ct('english')}</MenuItem>
            </TextField>
            <Stack direction={{ xs: 'column', sm: 'row' }} flexWrap="wrap" useFlexGap>
              {['email', 'sms', 'whatsapp'].map((channel) => (
                <FormControlLabel key={channel} label={ct(channel)} control={
                  <Checkbox checked={draft[`${channel}Consent`]} disabled={channel !== 'email' || !manage || saving}
                    onChange={(event) => change(`${channel}Consent`, event.target.checked)} />
                } />
              ))}
            </Stack>
            <Typography variant="body2" color="text.secondary">{ct('emailOnly')}</Typography>
            <SpaCommunicationChannels state={capabilities} />
            {success && <Alert severity="success">{ct('saved')}</Alert>}
            {manage && <Box><Button type="submit" variant="contained" disabled={!dirty || saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>{ct('save')}</Button></Box>}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
